import React from "react";
import { siteData } from "../../data/mock";
import {
  Gamepad2,
  Lock,
  Target,
  Heart,
  Zap,
  RotateCcw,
  Handshake,
  Trophy,
  Check,
  Hammer,
  HardHat,
  Shield,
} from "lucide-react";

const focusIcons = [Target, Heart, Zap, RotateCcw, Handshake];

const SystemSection = () => {
  const { system, images } = siteData;
  return (
    <section id="system" className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7dfabf]/20 bg-[#7dfabf]/5 mb-6">
            <Gamepad2 size={16} className="text-[#7dfabf]" />
            <span className="text-sm text-[#7dfabf] font-medium tracking-wider uppercase font-body">
              The System
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight font-heading">
            {system.title}
          </h2>
          <p className="text-lg text-white/60 max-w-3xl mx-auto leading-relaxed font-body">
            {system.subtitle}
          </p>
        </div>

        {/* Methodology Diagram */}
        <div className="flex justify-center mb-16">
          <img
            src={images.methodology.url}
            alt={images.methodology.alt}
            className="w-full max-w-2xl rounded-2xl"
            loading="lazy"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="p-8 rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/15 transition-all duration-500">
            <div className="flex items-center gap-3 mb-6">
              <Lock size={20} className="text-[#7dfabf]" />
              <h3 className="text-lg font-bold text-white font-heading">The Rules</h3>
            </div>
            <ul className="space-y-4">
              {system.rules.map((rule, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#7dfabf]" />
                  <span className="text-white/80 font-body">{rule}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/15 transition-all duration-500">
            <div className="flex items-center gap-3 mb-6">
              <Trophy size={20} className="text-[#7dfabf]" />
              <h3 className="text-lg font-bold text-white font-heading">Each Phase is Designed To</h3>
            </div>
            <ul className="space-y-4">
              {system.benefits.map((benefit, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#7dfabf]/10 flex items-center justify-center flex-shrink-0">
                    <Check size={14} className="text-[#7dfabf]" />
                  </div>
                  <span className="text-white/80 font-body">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="text-center p-6 rounded-2xl bg-[#7dfabf]/5 border border-[#7dfabf]/20">
          <p className="text-xl text-[#7dfabf] font-semibold font-body">
            {system.highlight}
          </p>
        </div>
      </div>
    </section>
  );
};

const FocusSection = () => {
  const { focus } = siteData;
  return (
    <section className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-4xl lg:text-5xl font-black text-white text-center mb-16 tracking-tight font-heading">
          {focus.title}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {focus.areas.map((area, i) => {
            const Icon = focusIcons[i];
            return (
              <div
                key={i}
                className="group p-8 rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/20 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-[#7dfabf]/10 flex items-center justify-center mb-6 group-hover:bg-[#7dfabf]/15 transition-colors duration-500">
                  <Icon size={24} className="text-[#7dfabf]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3 font-heading">{area.title}</h3>
                <p className="text-white/60 leading-relaxed font-body">{area.description}</p>
              </div>
            );
          })}
        </div>
        <p className="text-center text-lg text-[#7dfabf] font-semibold mt-12 font-body">
          {focus.bottomLine}
        </p>
      </div>
    </section>
  );
};

const OutcomesSection = () => {
  const { outcomes } = siteData;
  return (
    <section id="outcomes" className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight font-heading">
          {outcomes.title}
        </h2>
        <p className="text-lg text-white/60 mb-12 font-body">{outcomes.subtitle}</p>
        <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-12">
          {outcomes.results.map((result, i) => (
            <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/15 transition-colors duration-300 text-left group">
              <div className="mt-0.5 w-7 h-7 rounded-lg bg-[#7dfabf]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#7dfabf]/20 transition-colors duration-300">
                <Check size={16} className="text-[#7dfabf]" />
              </div>
              <span className="text-white/80 leading-relaxed font-body">{result}</span>
            </div>
          ))}
        </div>
        <div className="p-6 rounded-2xl bg-[#7dfabf]/5 border border-[#7dfabf]/20">
          <p className="text-xl text-[#7dfabf] font-semibold font-body">{outcomes.highlight}</p>
        </div>
      </div>
    </section>
  );
};

const phaseIcons = [Hammer, HardHat, Shield];

const StructureSection = () => {
  const { structure } = siteData;
  return (
    <section id="structure" className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight font-heading">
            {structure.title}
          </h2>
          <p className="text-xl text-white/60 font-body">{structure.subtitle}</p>
        </div>
        <div className="space-y-8">
          {structure.phases.map((phase, i) => {
            const Icon = phaseIcons[i];
            return (
              <div
                key={i}
                className="group relative p-8 lg:p-10 rounded-2xl bg-[#0A0A0A] border border-white/5 hover:border-[#7dfabf]/20 transition-all duration-500"
              >
                <div className="absolute top-6 right-8 text-7xl font-black text-[#7dfabf]/5 select-none font-heading">
                  {phase.number}
                </div>
                <div className="relative">
                  <div className="flex items-start gap-5 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[#7dfabf]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#7dfabf]/15 transition-colors duration-500">
                      <Icon size={28} className="text-[#7dfabf]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-2xl font-bold text-white font-heading">
                          Phase {phase.number} — {phase.name}
                        </h3>
                        <span className="px-3 py-1 rounded-full bg-[#7dfabf]/10 text-[#7dfabf] text-sm font-medium font-body">
                          {phase.weeks}
                        </span>
                      </div>
                      <p className="text-[#7dfabf] font-semibold mt-1 font-body">{phase.tagline}</p>
                    </div>
                  </div>
                  <p className="text-white/60 leading-relaxed mb-4 ml-0 lg:ml-[76px] font-body">
                    {phase.description}
                  </p>
                  <p className="text-white/80 italic ml-0 lg:ml-[76px] font-body">
                    You'll feel: {phase.feel}
                  </p>
                </div>
                {i < structure.phases.length - 1 && (
                  <div className="hidden lg:block absolute -bottom-8 left-1/2 -translate-x-1/2 w-px h-8 bg-gradient-to-b from-[#7dfabf]/20 to-transparent" />
                )}
              </div>
            );
          })}
        </div>
        <p className="text-center text-lg text-[#7dfabf] font-semibold mt-12 font-body">
          {structure.summary}
        </p>
      </div>
    </section>
  );
};

export const ProgramSections = () => {
  return (
    <>
      <SystemSection />
      <FocusSection />
      <OutcomesSection />
      <StructureSection />
    </>
  );
};
